import { useState, useMemo } from "react";
import { NumberField, Stat, Donut, ToolShell } from "./parts";
import { inr, inrShort } from "@/lib/format";

export function RentalYieldCalculator() {
  const [value, setValue] = useState(6000000);
  const [monthlyRent, setMonthlyRent] = useState(22000);
  const [annualExpenses, setAnnualExpenses] = useState(40000);

  const { annualRent, gross, net } = useMemo(() => {
    const annualRent = monthlyRent * 12;
    const gross = value ? (annualRent / value) * 100 : 0;
    const net = value ? ((annualRent - annualExpenses) / value) * 100 : 0;
    return { annualRent, gross, net };
  }, [value, monthlyRent, annualExpenses]);

  return (
    <ToolShell title="Rental Yield Calculator" desc="See how hard your property works for you. Gross and net rental yield tell you the annual return from rent alone.">
      <div className="grid gap-10 lg:grid-cols-2">
        <div className="space-y-8">
          <NumberField label="Property Value" value={value} onChange={setValue} min={500000} max={200000000} step={100000} prefix="₹" testid="ry-value" />
          <NumberField label="Monthly Rent" value={monthlyRent} onChange={setMonthlyRent} min={1000} max={1000000} step={1000} prefix="₹" testid="ry-rent" />
          <NumberField label="Annual Expenses (tax, maint.)" value={annualExpenses} onChange={setAnnualExpenses} min={0} max={2000000} step={5000} prefix="₹" testid="ry-expenses" />
        </div>
        <div className="space-y-5">
          <Stat label="Gross Rental Yield" value={gross.toFixed(2) + "%"} highlight testid="ry-result" />
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Net Rental Yield" value={net.toFixed(2) + "%"} />
            <Stat label="Annual Rent" value={inrShort(annualRent)} />
          </div>
          <Donut data={[{ name: "Net Rent Income", value: Math.max(0, annualRent - annualExpenses) }, { name: "Expenses", value: annualExpenses }]} />
          <p className="border border-border bg-background p-4 font-mono text-xs leading-relaxed text-muted-foreground">
            A healthy residential rental yield in Indian metros is typically 2.5-4%. Commercial properties often yield higher.
          </p>
        </div>
      </div>
    </ToolShell>
  );
}
